import { useCallback, useMemo, useRef, useState } from 'react';
import { Sentry } from '@idk-web/core-utils';
import { useIsMounted } from '@idk-web/core-ui';
import { logoutPortal, PortalLoginStatusResponse } from '@idk-web/api';
import { isBankIDError } from '@/utils/error';
import { login as stateLogin, logout as stateLogout } from '@/state/portalAuth';
import * as portalLogin from '@/hooks/portal/login/portalLogin';
import { StartedPortalLoginRequest } from '@/hooks/portal/login/portalLogin';

type State =
  | {
      state: 'IDLE';
    }
  | {
      state: 'CANCELLED';
    }
  | {
      state: 'TIMED_OUT';
    }
  | {
      state: 'WAITING';
      bankId?: {
        autoStartToken: string;
        qrData: string;
      };
      freja?: {
        qrData: string;
      };
    }
  | {
      state: 'COMPLETE';
    }
  | {
      state: 'FAILED';
      error: unknown;
    };

export type PortalLogin = {
  loginPassword(username: string, password: string): Promise<void>;
  loginBankIdSe(): Promise<void>;
  loginFreja(): Promise<void>;
  loginBankIdNo(window: Window): Promise<void>;
  loginMitId(window: Window): Promise<void>;
  loginFtn(window: Window): Promise<void>;
  logout(): Promise<void>;
  cancel(): Promise<void>;
  reset(): void;
} & State;

export function usePortalLogin(): PortalLogin {
  const isMounted = useIsMounted();
  const request = useRef<StartedPortalLoginRequest>(undefined);
  const [state, setState] = useState<State>({ state: 'IDLE' });
  const onInit = useCallback((data: PortalLoginStatusResponse) => {
    switch (data.status) {
      case 'COMPLETED':
        return onComplete(data);
      case 'IN_PROGRESS':
        return onProgress(data);
      case 'FAILED':
        return onError(data);
    }
  }, []);
  const onComplete = useCallback(
    async (
      result: Extract<PortalLoginStatusResponse, { status: 'COMPLETED' }>,
    ) => {
      stateLogin(result);

      if (isMounted()) {
        setState({ state: 'COMPLETE' });
      }

      request.current = undefined;
    },
    [],
  );
  const onError = useCallback(async (error: unknown) => {
    if (isMounted()) {
      if (error === 'cancelled') {
        setState({ state: 'CANCELLED' });
      } else if (error === 'timed out') {
        setState({ state: 'TIMED_OUT' });
      } else {
        if (!isBankIDError(error)) {
          Sentry.reportError('Portal login failed', { error });
        }

        setState({ state: 'FAILED', error });
      }
    }

    request.current = undefined;
  }, []);
  const onProgress = useCallback(
    async (
      data: Extract<PortalLoginStatusResponse, { status: 'IN_PROGRESS' }>,
    ) => {
      setState((oldState) => {
        let newState: State;
        if (data.bankId) {
          newState = { state: 'WAITING', bankId: data.bankId };
        } else if (data.freja) {
          newState = { state: 'WAITING', freja: data.freja };
        } else {
          newState = { state: 'WAITING' };
        }

        if (JSON.stringify(oldState) === JSON.stringify(newState)) {
          return oldState;
        } else {
          return newState;
        }
      });
    },
    [],
  );
  const loginPassword = useCallback(
    async (username: string, password: string): Promise<void> => {
      if (request.current) {
        return;
      }

      setState({ state: 'WAITING' });

      request.current = portalLogin
        .password({ username, password })
        .onInit(onInit)
        .onComplete(onComplete)
        .onError(onError)
        .onProgress(onProgress)
        .start();

      return request.current.wait();
    },
    [onInit, onComplete, onError, onProgress],
  );
  const loginBankIdSe = useCallback(async (): Promise<void> => {
    if (request.current) {
      return;
    }

    setState({ state: 'WAITING' });

    request.current = portalLogin
      .bankIdSe()
      .onInit(onInit)
      .onComplete(onComplete)
      .onError(onError)
      .onProgress(onProgress)
      .start();

    return request.current.wait();
  }, [onInit, onComplete, onError, onProgress]);
  const loginFreja = useCallback(async (): Promise<void> => {
    if (request.current) {
      return;
    }

    setState({ state: 'WAITING' });

    request.current = portalLogin
      .freja()
      .onInit(onInit)
      .onComplete(onComplete)
      .onError(onError)
      .onProgress(onProgress)
      .start();

    return request.current.wait();
  }, [onInit, onComplete, onError, onProgress]);
  const loginBankIdNo = useCallback(
    async (window: Window): Promise<void> => {
      if (request.current) {
        return;
      }

      setState({ state: 'WAITING' });

      request.current = portalLogin
        .bankIdNo(window)
        .onInit(onInit)
        .onComplete(onComplete)
        .onError(onError)
        .onProgress(onProgress)
        .start();

      return request.current.wait();
    },
    [onInit, onComplete, onError, onProgress],
  );
  const loginMitId = useCallback(
    async (window: Window): Promise<void> => {
      if (request.current) {
        return;
      }

      setState({ state: 'WAITING' });

      request.current = portalLogin
        .mitId(window)
        .onInit(onInit)
        .onComplete(onComplete)
        .onError(onError)
        .onProgress(onProgress)
        .start();

      return request.current.wait();
    },
    [onInit, onComplete, onError, onProgress],
  );
  const loginFtn = useCallback(
    async (window: Window): Promise<void> => {
      if (request.current) {
        return;
      }

      setState({ state: 'WAITING' });

      request.current = portalLogin
        .ftn(window)
        .onInit(onInit)
        .onComplete(onComplete)
        .onError(onError)
        .onProgress(onProgress)
        .start();

      return request.current.wait();
    },
    [onInit, onComplete, onError, onProgress],
  );
  const logout = useCallback(async (): Promise<void> => {
    try {
      await logoutPortal();
    } catch (e) {
      console.warn('Failed to delete session: Already expired', { error: e });
    }

    stateLogout();
    reset();
  }, []);
  const cancel = useCallback(async () => {
    if (isMounted()) {
      setState({ state: 'CANCELLED' });
    }

    return request.current?.cancel();
  }, []);
  const reset = useCallback(() => {
    if (isMounted()) {
      setState({ state: 'IDLE' });
    }

    request.current = undefined;
  }, []);

  return useMemo(
    () => ({
      loginPassword,
      loginBankIdSe,
      loginFreja,
      loginBankIdNo,
      loginMitId,
      loginFtn,
      logout,
      cancel,
      reset,
      ...state,
    }),
    [
      loginPassword,
      loginBankIdSe,
      loginFreja,
      loginBankIdNo,
      loginMitId,
      loginFtn,
      logout,
      cancel,
      reset,
      state,
    ],
  );
}
